import React, { useState } from 'react';
import { CheckCircle, Circle, ChevronDown, ChevronUp, Zap, Thermometer, Server, Globe, ArrowRight } from 'lucide-react';
import { LocationData } from '../types';

interface Step {
  id: string;
  label: string;
  detail: string;
  done: boolean;
  action?: () => void;
  actionLabel?: string;
  icon: React.ReactNode;
}

interface SetupChecklistProps {
  location: LocationData;
  hasFloorWithRack: boolean;
  setShowPowerManagementModal: (v: boolean) => void;
  setShowChillerManagementModal: (v: boolean) => void;
  onOpenMmr: () => void;
  setShowMmrModal: (v: boolean) => void;
  setShowNetworkModal: (v: boolean) => void;
  // Guided actions for the in-floor steps — select the right rack and scroll the
  // server/network panel into view so the player never has to hunt for it.
  onSelectRackForPurchase: () => void;
  onSelectRackForMpo: () => void;
  onSelectRackForTor: () => void;
}

const SetupChecklist: React.FC<SetupChecklistProps> = ({
  location,
  hasFloorWithRack,
  setShowPowerManagementModal,
  setShowChillerManagementModal,
  onOpenMmr,
  setShowMmrModal,
  setShowNetworkModal,
  onSelectRackForPurchase,
  onSelectRackForMpo,
  onSelectRackForTor,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  const hasSupplier = !!location.selectedPowerSupplierId;
  const hasTransformer = !!location.transformer;
  const hasPowerhouse = !!location.powerhouse?.installed;
  const hasUps = !!location.ups?.installed;
  const hasCooling = (location.totalCoolingCapacityKW || 0) > 0;

  const allRacks = Object.values(location.floors ?? {}).flatMap(f => f.racks ?? []);
  const hasMpo = allRacks.some(r => r.mpoPatchPanel);
  const hasTor = allRacks.some(r => r.torSwitch);

  const mmrBuilt = !!location.mmr?.built;
  const hasCoreSwitch = !!location.coreSwitch;
  const hasTransit =
    (location.mmr?.transitProviders ?? []).length > 0 ||
    (location.mmr?.ixpConnections ?? []).length > 0;

  const steps: Step[] = [
    {
      id: 'supplier',
      label: 'Choose a power supplier',
      detail: 'Select a utility provider to get electricity rates.',
      done: hasSupplier,
      action: () => setShowPowerManagementModal(true),
      actionLabel: 'Open Power',
      icon: <Zap className="w-4 h-4" />,
    },
    {
      id: 'transformer',
      label: 'Install Transformer',
      detail: 'Steps the grid voltage down to usable levels.',
      done: hasTransformer,
      action: () => setShowPowerManagementModal(true),
      actionLabel: 'Open Power',
      icon: <Zap className="w-4 h-4" />,
    },
    {
      id: 'powerhouse',
      label: 'Install Power Distribution',
      detail: 'The MDB / switchgear distributes power across the facility.',
      done: hasPowerhouse,
      action: () => setShowPowerManagementModal(true),
      actionLabel: 'Open Power',
      icon: <Zap className="w-4 h-4" />,
    },
    {
      id: 'ups',
      label: 'Install UPS',
      detail: 'Uninterruptible power supply keeps servers online during outages.',
      done: hasUps,
      action: () => setShowPowerManagementModal(true),
      actionLabel: 'Open Power',
      icon: <Zap className="w-4 h-4" />,
    },
    {
      id: 'cooling',
      label: 'Build Cooling Chain',
      detail: 'Heat Rejection → Chiller → CRAH must all be installed to cool racks.',
      done: hasCooling,
      action: () => setShowChillerManagementModal(true),
      actionLabel: 'Open Cooling',
      icon: <Thermometer className="w-4 h-4" />,
    },
    {
      id: 'rack',
      label: 'Purchase your first rack',
      detail: 'Pick an empty slot on the floor grid, then choose a rack type to buy. The button below selects a free slot and jumps you to the purchase panel.',
      done: hasFloorWithRack,
      action: onSelectRackForPurchase,
      actionLabel: 'Pick a slot',
      icon: <Server className="w-4 h-4" />,
    },
    {
      id: 'mpo',
      label: 'Install an MPO patch panel',
      detail: 'The MPO panel (U48) is required before a TOR switch can be fitted. The button selects your rack and opens the install panel.',
      done: hasMpo,
      action: onSelectRackForMpo,
      actionLabel: 'Open rack',
      icon: <Server className="w-4 h-4" />,
    },
    {
      id: 'tor',
      label: 'Install a TOR switch',
      detail: 'The TOR switch (U46) connects your rack to the network so servers can carry traffic. The button selects your rack and opens the install panel.',
      done: hasTor,
      action: onSelectRackForTor,
      actionLabel: 'Open rack',
      icon: <Server className="w-4 h-4" />,
    },
    {
      id: 'mmr',
      label: 'Build the Meet-Me Room',
      detail: 'The MMR is where internet connectivity enters your datacenter. Required before any server earns revenue.',
      done: mmrBuilt,
      action: () => onOpenMmr(),
      actionLabel: 'Open MMR',
      icon: <Globe className="w-4 h-4" />,
    },
    {
      id: 'coreswitch',
      label: 'Install a Core Switch',
      detail: 'The core switch is required before ordering transit or IXP connections. Install it via Data Network → Core Switch.',
      done: hasCoreSwitch,
      action: () => setShowNetworkModal(true),
      actionLabel: 'Open Network',
      icon: <Globe className="w-4 h-4" />,
    },
    {
      id: 'transit',
      label: 'Connect to a transit provider or IXP',
      detail: 'Without external connectivity your servers earn $0. Order transit bandwidth or join an Internet Exchange.',
      done: hasTransit,
      action: () => setShowMmrModal(true),
      actionLabel: 'Order Connectivity',
      icon: <Globe className="w-4 h-4" />,
    },
  ];

  const doneCount = steps.filter(s => s.done).length;
  const allDone = doneCount === steps.length;

  if (allDone) return null;

  const nextStep = steps.find(s => !s.done);

  return (
    <div id="setup-guide" className="mb-4 bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-750 transition-colors"
        onClick={() => setCollapsed(c => !c)}
      >
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-white">Setup Guide</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-900 text-blue-300">
            {doneCount}/{steps.length} complete
          </span>
          {nextStep && !collapsed && (
            <span className="flex items-center gap-1 text-xs text-amber-400">
              <ArrowRight className="w-3 h-3" />
              Next: {nextStep.label}
            </span>
          )}
        </div>
        {collapsed ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronUp className="w-4 h-4 text-gray-400" />}
      </button>

      {/* Progress bar */}
      <div className="h-1 bg-gray-700">
        <div
          className="h-1 bg-blue-500 transition-all duration-500"
          style={{ width: `${(doneCount / steps.length) * 100}%` }}
        />
      </div>

      {/* Steps */}
      {!collapsed && (
        <div className="divide-y divide-gray-700">
          {steps.map((step, i) => {
            const prevDone = i === 0 || steps[i - 1].done;
            const isNext = !step.done && prevDone;
            return (
              <div
                key={step.id}
                className={`flex items-start gap-3 px-4 py-3 transition-colors ${
                  step.done
                    ? 'opacity-50'
                    : isNext
                    ? 'bg-gray-750'
                    : 'opacity-40'
                }`}
              >
                {/* Icon */}
                <div className={`mt-0.5 flex-shrink-0 ${step.done ? 'text-emerald-400' : isNext ? 'text-blue-400' : 'text-gray-600'}`}>
                  {step.done ? <CheckCircle className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium ${step.done ? 'line-through text-gray-500' : isNext ? 'text-white' : 'text-gray-500'}`}>
                    {step.label}
                  </div>
                  {isNext && (
                    <div className="text-xs text-gray-400 mt-0.5">{step.detail}</div>
                  )}
                </div>

                {/* Action button */}
                {isNext && step.action && (
                  <button
                    onClick={step.action}
                    className="flex-shrink-0 text-xs px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded transition-colors"
                  >
                    {step.actionLabel}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SetupChecklist;
